import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ShieldCheck, ArrowLeft, AlertTriangle, Clock } from 'lucide-react'
import clsx from 'clsx'
import { getFleetAssurance } from '../../services/api'

// Trust Score is a MATURING metric: a young farm is expected to start lower and
// climb, so a ranch is graded against its tenure-based expected score, not an
// absolute pass mark. These helpers keep that framing in the UI.
function scoreBadge(r) {
  if (r.score == null) return { label: 'no data yet', cls: 'bg-gray-100 text-gray-500' }
  const behind = r.expected_score != null && r.score < r.expected_score - 15
  const cls = r.on_track && r.score >= 70
    ? 'bg-green-100 text-green-800'
    : behind
      ? 'bg-red-100 text-red-800'
      : 'bg-amber-100 text-amber-800'
  return { label: `${Math.round(r.score)} · ${r.band}`, cls }
}

function trajectoryLabel(r) {
  if (r.score == null || r.expected_score == null) return null
  const d = r.score - r.expected_score
  if (d >= 8) return { text: 'ahead of stage', cls: 'text-green-600' }
  if (d <= -8) return { text: 'behind stage', cls: 'text-red-600' }
  return { text: 'on track', cls: 'text-gray-500' }
}

function Num({ value, warn }) {
  const v = value || 0
  return (
    <span className={clsx('font-medium', v > 0 && warn ? 'text-red-600' : v > 0 ? 'text-gray-900' : 'text-gray-300')}>
      {v}
    </span>
  )
}

function SummaryCard({ label, value, icon: Icon, tone = 'gray' }) {
  const tones = {
    gray: 'text-gray-500',
    amber: 'text-amber-600',
    red: 'text-red-600',
  }
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 text-xs font-medium text-gray-500 uppercase tracking-wider">
        {Icon && <Icon className={clsx('h-4 w-4', tones[tone])} />}
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
    </div>
  )
}

export default function FleetAssurance() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['fleetAssurance'],
    queryFn: ({ signal }) => getFleetAssurance({ signal }),
  })

  const ranches = data?.ranches || []
  const needAttention = ranches.filter(r => (r.overdue || 0) > 0 || (r.critical_open || 0) > 0).length
  const totalOverdue = ranches.reduce((s, r) => s + (r.overdue || 0), 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/dashboard" className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="h-5 w-5 text-gray-500" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary-600" />
            Fleet Assurance
          </h1>
          <p className="text-gray-500 mt-1">
            Every ranch ranked by data-integrity risk — the farms that most need a look.
            Trust Score is a maturing metric: judge it against each farm's expected stage,
            not as a grade.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          {error.message || 'Failed to load fleet assurance data.'}
        </div>
      )}

      {!error && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <SummaryCard label="Ranches" value={ranches.length} icon={ShieldCheck} />
          <SummaryCard label="Need attention" value={needAttention} icon={AlertTriangle}
            tone={needAttention > 0 ? 'amber' : 'gray'} />
          <SummaryCard label="Overdue exceptions" value={totalOverdue} icon={Clock}
            tone={totalOverdue > 0 ? 'red' : 'gray'} />
        </div>
      )}

      <div className="card overflow-hidden">
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading fleet…</p>
        ) : ranches.length === 0 ? (
          <p className="p-6 text-gray-500">
            No ranches yet. Trust scores appear after the first assurance scan runs.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ranch</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trust Score</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Open</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Critical</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Overdue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Oldest overdue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" title="Registration at the crush: share of the active herd wearing a bound tag, share the reviewer confirmed, bindings sent back and open, days since day 0 (the herd baseline date, else the ranch's creation)">Registration</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Onboarded</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {ranches.map((r) => {
                  const badge = scoreBadge(r)
                  const traj = trajectoryLabel(r)
                  const oldest = r.oldest_overdue
                    ? Math.max(0, Math.round((Date.now() - new Date(r.oldest_overdue).getTime()) / 86400000))
                    : null
                  return (
                    <tr key={r.ranch_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{r.name || r.code || '—'}</div>
                        {r.code && <div className="text-xs text-gray-400">{r.code}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={clsx('px-2 py-1 text-xs font-medium rounded-full capitalize', badge.cls)}>
                          {badge.label}
                        </span>
                        {traj && <div className={clsx('text-xs mt-1', traj.cls)}>{traj.text}</div>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm"><Num value={r.open_exceptions} /></td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm"><Num value={r.critical_open} warn /></td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm"><Num value={r.overdue} warn /></td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {oldest != null ? `${oldest}d ago` : '—'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm" data-testid="fleet-registration">
                        {r.registration ? (
                          <>
                            <div className={clsx('font-medium', (r.registration.coverage_pct ?? 0) >= 95 ? 'text-green-700' : 'text-gray-900')}>
                              {r.registration.coverage_pct != null ? `${r.registration.coverage_pct}% covered` : 'no herd'}
                              {r.registration.confirmed_pct != null && <span className="text-gray-500 font-normal"> · {r.registration.confirmed_pct}% confirmed</span>}
                            </div>
                            <div className="text-xs text-gray-500">
                              <span className={r.registration.sent_back_open > 0 ? 'text-red-600' : undefined}>{r.registration.sent_back_open} sent back</span>
                              {r.registration.day != null && ` · day ${r.registration.day}`}
                            </div>
                          </>
                        ) : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-500">
                        {r.days_since_onboarding != null ? `${r.days_since_onboarding}d` : '—'}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
